import { useState, useEffect, useCallback } from 'react';

/**
 * Device detection hook for Transcend Platform
 * Provides comprehensive device type detection including:
 * - Screen size breakpoints
 * - Touch capability detection
 * - Orientation tracking
 */

// Breakpoint constants
const BREAKPOINTS = {
  MOBILE_MAX: 767,
  TABLET_MIN: 768,
  TABLET_MAX: 1366,
  DESKTOP_MIN: 1367
};

// Device types
export const DEVICE_TYPES = {
  MOBILE: 'mobile',
  TABLET: 'tablet',
  DESKTOP: 'desktop'
};

/**
 * Determines device type based on screen width and touch capability
 */
const getDeviceType = (width, isTouch) => {
  // Mobile: < 768px
  if (width <= BREAKPOINTS.MOBILE_MAX) {
    return DEVICE_TYPES.MOBILE;
  }

  // Tablet: 768px - 1366px OR touch-primary device in that range
  // Also consider touch devices up to 1366px as tablets (iPad Pro landscape)
  if (width >= BREAKPOINTS.TABLET_MIN && width <= BREAKPOINTS.TABLET_MAX) {
    return DEVICE_TYPES.TABLET;
  }

  // Large touch devices (like iPad Pro 12.9" landscape at 1366px) should be tablet
  if (isTouch && width <= BREAKPOINTS.TABLET_MAX) {
    return DEVICE_TYPES.TABLET;
  }

  // Desktop: > 1366px with mouse/trackpad
  return DEVICE_TYPES.DESKTOP;
};

/**
 * Gets orientation based on window dimensions
 */
const getOrientation = () => {
  if (typeof window === 'undefined') return 'portrait';
  return window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
};

/**
 * Detects if the device has touch capability
 */
const detectTouch = () => {
  if (typeof window === 'undefined') return false;

  // Primary check: pointer: coarse media query (most reliable)
  const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches;

  // Fallback checks
  const hasTouchPoints = navigator.maxTouchPoints > 0;
  const hasTouch = 'ontouchstart' in window;

  return hasCoarsePointer || hasTouchPoints || hasTouch;
};

/**
 * Detects if device has hover capability (mouse/trackpad)
 */
const detectHover = () => {
  if (typeof window === 'undefined') return true;
  return window.matchMedia('(hover: hover)').matches;
};

/**
 * Custom hook for device context
 * @returns {Object} Device information
 */
export const useDeviceDetection = () => {
  const [state, setState] = useState(() => {
    const width = typeof window !== 'undefined' ? window.innerWidth : 1200;
    const isTouch = detectTouch();

    return {
      screenWidth: width,
      screenHeight: typeof window !== 'undefined' ? window.innerHeight : 800,
      deviceType: getDeviceType(width, isTouch),
      isTouch,
      hasHover: detectHover(),
      orientation: getOrientation()
    };
  });

  const updateDeviceInfo = useCallback(() => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const isTouch = detectTouch();

    setState({
      screenWidth: width,
      screenHeight: height,
      deviceType: getDeviceType(width, isTouch),
      isTouch,
      hasHover: detectHover(),
      orientation: getOrientation()
    });
  }, []);

  useEffect(() => {
    // Initial update
    updateDeviceInfo();

    // Listen for resize events (debounced)
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateDeviceInfo, 100);
    };

    // Listen for orientation changes
    const handleOrientationChange = () => {
      // Small delay to let the browser settle
      setTimeout(updateDeviceInfo, 100);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);

    // Listen for pointer/hover capability changes (rare but possible with docking)
    const pointerQuery = window.matchMedia('(pointer: coarse)');
    const hoverQuery = window.matchMedia('(hover: hover)');

    const handlePointerChange = () => updateDeviceInfo();

    // Modern browsers
    if (pointerQuery.addEventListener) {
      pointerQuery.addEventListener('change', handlePointerChange);
      hoverQuery.addEventListener('change', handlePointerChange);
    }

    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);

      if (pointerQuery.removeEventListener) {
        pointerQuery.removeEventListener('change', handlePointerChange);
        hoverQuery.removeEventListener('change', handlePointerChange);
      }
    };
  }, [updateDeviceInfo]);

  // Convenience booleans
  // Tablets now use mobile UI - isMobile includes both mobile and tablet devices
  const isActualMobile = state.deviceType === DEVICE_TYPES.MOBILE;
  const isActualTablet = state.deviceType === DEVICE_TYPES.TABLET;
  const isMobile = isActualMobile || isActualTablet;  // Tablets use mobile UI
  const isTablet = isActualTablet;  // Keep for reference if needed
  const isDesktop = state.deviceType === DEVICE_TYPES.DESKTOP;
  const isPortrait = state.orientation === 'portrait';
  const isLandscape = state.orientation === 'landscape';

  return {
    ...state,
    isMobile,
    isTablet,
    isDesktop,
    isPortrait,
    isLandscape,
    // Utility function for page size
    getPageSize: () => {
      if (isMobile) return 25;  // Mobile and tablet
      return 100;
    }
  };
};

export default useDeviceDetection;
